import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import MaintenanceMode from './components/MaintenanceMode'
import CustomCursor from './components/CustomCursor'
import HomepageBackgroundManager from './components/HomepageBackgroundManager'
import MatchdayPortal from './components/MatchdayPortal'
import SeasonDashboardPortal from './components/SeasonDashboardPortal'
import MediaWallPreviewPortal from './components/MediaWallPreviewPortal'
import AcademyPathwayPortal from './components/AcademyPathwayPortal'
import SponsorPlacementPortal from './components/SponsorPlacementPortal'
import SponsorIntelligencePortal from './components/admin/SponsorIntelligencePortal'
import HomePage from './pages/HomePage'
import TeamPage from './pages/TeamPage'
import PlayerProfilePage from './pages/PlayerProfilePage'
import MatchesPage from './pages/MatchesPage'
import MatchCenterPage from './pages/MatchCenterPage'
import AcademyPage from './pages/AcademyPage'
import NewsPage from './pages/NewsPage'
import NewsDetailPage from './pages/NewsDetailPage'
import MediaWallPage from './pages/MediaWallPage'
import ContactPageSecure from './pages/ContactPageSecure'
import TeamPolicyPage from './pages/TeamPolicyPage'
import AcademyRegistrationPageSecure from './pages/AcademyRegistrationPageSecure'
import AdminLogin from './pages/admin/AdminLogin'
import Dashboard from './pages/admin/Dashboard'
import SiteSettingsBackgrounds from './pages/admin/SiteSettingsBackgrounds'
import FormsSettings from './pages/admin/FormsSettings'
import MaintenanceSettings from './pages/admin/MaintenanceSettings'
import TeamMembers from './pages/admin/TeamMembers'
import PlayerAgeOverrides from './pages/admin/PlayerAgeOverrides'
import MatchesHub from './pages/admin/MatchesHub'
import News from './pages/admin/News'
import Academy from './pages/admin/Academy'
import Tournaments from './pages/admin/Tournaments'
import Gallery from './pages/admin/Gallery'
import Standings from './pages/admin/Standings'
import Sponsors from './pages/admin/Sponsors'
import ContactFormsTracking from './pages/admin/ContactFormsTracking'
import AcademyRegistrationsTracking from './pages/admin/AcademyRegistrationsTracking'
import EditableContentManager from './pages/admin/EditableContentManager'
import UserManagement from './pages/admin/UserManagement'
import AdminRoute from './components/AdminRoute'
import PublicLayout from './components/PublicLayout'
import { SiteProvider } from './contexts/SiteContext'
import { useSiteConfig } from './hooks/useSupabase'
import './App.css'

if (typeof window !== 'undefined' && window.__SENTRY__) {
  window.__SENTRY__.hub = {
    captureException: () => {},
    captureMessage: () => {},
    addBreadcrumb: () => {},
  }
}

function AppContent() {
  const { data: siteConfigData, loading: configLoading } = useSiteConfig()
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)
  const [hasBypass, setHasBypass] = useState(false)

  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']")
    if (link) link.href = './favicon.ico'
  }, [])

  useEffect(() => {
    if (!configLoading && siteConfigData && siteConfigData.length > 0) {
      const config = siteConfigData[0]
      const maintenanceEnabled = config.maintenance_mode || false
      const bypassExists = localStorage.getItem('maintenance_bypass') === 'true'
      setIsMaintenanceMode(maintenanceEnabled)
      setHasBypass(bypassExists)
    }
  }, [siteConfigData, configLoading])

  if (isMaintenanceMode && !hasBypass && !configLoading) {
    if (!window.location.pathname.includes('/admin/')) {
      return <MaintenanceMode />
    }
  }

  return (
    <Router>
      <HomepageBackgroundManager />
      <MatchdayPortal />
      <SeasonDashboardPortal />
      <MediaWallPreviewPortal />
      <AcademyPathwayPortal />
      <SponsorPlacementPortal />
      <SponsorIntelligencePortal />
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
        <Route path="/admin/settings" element={<AdminRoute><SiteSettingsBackgrounds /></AdminRoute>} />
        <Route path="/admin/forms-settings" element={<AdminRoute><FormsSettings /></AdminRoute>} />
        <Route path="/admin/maintenance" element={<AdminRoute><MaintenanceSettings /></AdminRoute>} />
        <Route path="/admin/team" element={<AdminRoute><TeamMembers /></AdminRoute>} />
        <Route path="/admin/player-ages" element={<AdminRoute><PlayerAgeOverrides /></AdminRoute>} />
        <Route path="/admin/matches" element={<AdminRoute><MatchesHub /></AdminRoute>} />
        <Route path="/admin/news" element={<AdminRoute><News /></AdminRoute>} />
        <Route path="/admin/academy" element={<AdminRoute><Academy /></AdminRoute>} />
        <Route path="/admin/tournaments" element={<AdminRoute><Tournaments /></AdminRoute>} />
        <Route path="/admin/gallery" element={<AdminRoute><Gallery /></AdminRoute>} />
        <Route path="/admin/standings" element={<AdminRoute><Standings /></AdminRoute>} />
        <Route path="/admin/sponsors" element={<AdminRoute><Sponsors /></AdminRoute>} />
        <Route path="/admin/contact-forms" element={<AdminRoute><ContactFormsTracking /></AdminRoute>} />
        <Route path="/admin/academy-registrations" element={<AdminRoute><AcademyRegistrationsTracking /></AdminRoute>} />
        <Route path="/admin/content" element={<AdminRoute><EditableContentManager /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />

        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/player/:id" element={<PlayerProfilePage />} />
          <Route path="/matches" element={<MatchesPage />} />
          <Route path="/matches/:id" element={<MatchCenterPage />} />
          <Route path="/academy" element={<AcademyPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:id" element={<NewsDetailPage />} />
          <Route path="/media" element={<MediaWallPage />} />
          <Route path="/contact" element={<ContactPageSecure />} />
          <Route path="/oroiomadas" element={<TeamPolicyPage />} />
          <Route path="/academy-registration" element={<AcademyRegistrationPageSecure />} />
        </Route>
      </Routes>
    </Router>
  )
}

function App() {
  return (
    <SiteProvider>
      <CustomCursor />
      <AppContent />
    </SiteProvider>
  )
}

export default App